// Comments section on a media item — any visitor can post; host can delete.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, Trash2, Reply } from "lucide-react";
import { Comments } from "../lib/api";

const NAME_KEY = "meyecam.display_name";

export default function CommentsSection({ mediaId, canModerate = false }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setList(await Comments.list(mediaId)); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    setName(localStorage.getItem(NAME_KEY) || "");
    load();
    // eslint-disable-next-line
  }, [mediaId]);

  const onPost = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const c = await Comments.post(mediaId, name.trim() || "Guest", text.trim());
      if (name.trim()) localStorage.setItem(NAME_KEY, name.trim());
      setList((prev) => [...prev, c]);
      setText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post");
    } finally { setPosting(false); }
  };

  const onDelete = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await Comments.remove(cid);
      setList((prev) => prev.filter((c) => c.id !== cid));
    } catch { toast.error("Could not delete"); }
  };

  const onReply = (author) => {
    setText((prev) => {
      const mention = `@${author} `;
      const stripped = prev.replace(/^@\S+\s*/, "");
      return mention + stripped;
    });
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <div className="glass-card p-4 mb-4" data-testid="comments-section">
      <div className="flex items-center gap-2 mb-3">
        <MessageCircle size={16} color="#22E2FF" />
        <div className="font-semibold">
          {list.length} {list.length === 1 ? "comment" : "comments"}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-4"><div className="meye-spinner" /></div>
      ) : list.length === 0 ? (
        <div className="text-sm py-2" style={{ color: "rgba(255,255,255,0.5)" }} data-testid="no-comments">
          Be the first to leave a comment.
        </div>
      ) : (
        <div className="flex flex-col gap-3 mb-3">
          {list.map((c) => (
            <div key={c.id} className="flex flex-col gap-1" data-testid={`comment-${c.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold" style={{ color: "rgba(255,255,255,0.92)" }}>
                  {c.author_name}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-[11px]" style={{ color: "rgba(255,255,255,0.4)" }}>
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                  <button
                    onClick={() => onReply(c.author_name)}
                    className="p-1 rounded"
                    style={{ color: "rgba(255,255,255,0.55)" }}
                    title="Reply"
                    data-testid={`reply-comment-${c.id}`}
                  >
                    <Reply size={12} />
                  </button>
                  {canModerate && (
                    <button
                      onClick={() => onDelete(c.id)}
                      className="p-1 rounded"
                      style={{ color: "#FF6B6B" }}
                      title="Delete comment"
                      data-testid={`delete-comment-${c.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
              <div className="text-sm whitespace-pre-wrap" style={{ color: "rgba(255,255,255,0.78)" }}>
                {c.text}
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={onPost} className="flex flex-col gap-2 pt-2" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <input
          className="meye-input"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={50}
          data-testid="comment-name-input"
          style={{ padding: "10px 12px", fontSize: 14 }}
        />
        <div className="flex gap-2">
          <input
            className="meye-input flex-1"
            placeholder="Add a comment…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={1000}
            data-testid="comment-text-input"
            ref={inputRef}
            style={{ padding: "10px 12px", fontSize: 14 }}
          />
          <button
            type="submit"
            className="cta-pill"
            disabled={posting || !text.trim()}
            data-testid="post-comment-btn"
            style={{ minHeight: 40, padding: "10px 16px" }}
          >
            {posting ? <div className="meye-spinner" /> : <Send size={14} />}
          </button>
        </div>
      </form>
    </div>
  );
}
